import { IFolderLayout } from './interfaces/folder-layout.interface';
import { Section } from './section';
import { SectionsGroup } from './sections-group';
import { ResizingStatus } from './server/enums/resizing-status';

/**
 * Représente la disposition des sections dans un dossier.
 */
export class FolderLayout implements IFolderLayout {
    /**
     * Liste des sections non affichées.
     */
    public nonDisplayedSections: Section[] = [];

    /**
     * Liste des groupes de sections (Il s'agit des colonnes divisant le détail d'un dossier, mais il pourrait s'agir de lignes).
     */
    public sectionsGroups: SectionsGroup[] = [];

    /**
     * Constructeur.
     * @param sectionGroupCount : Nombre de groupes de sections.
     */
    constructor(sectionGroupCount: number) {
        this.sectionsGroups = Array.from({ length: sectionGroupCount }, () => new SectionsGroup());
    }

    /**
     * Crée une instance de FolderLayout à partir d'un objet retourné par le serveur.
     * @param other : Instance de l'objet
     * @returns Une instane de FolderLayout
     */
    public static from(other: any) {
        if (!!other) {
            if (other.sectionsGroups && other.sectionsGroups.length) {
                const instance = new FolderLayout(other.sectionsGroups.length);
                instance.nonDisplayedSections = other.nonDisplayedSections;
                instance.sectionsGroups = other.sectionsGroups;
                return instance;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    /**
     * Masque une section.
     * remarque: La section est retirée du groupe de sections auquel elle appartenait et est ajoutée à la liste des sections non affichées.
     * @param sectionName : Nom de la section à masquer.
     */
    public removeSection(sectionName: string) {
        for (let index: number = 0; index < this.sectionsGroups.length; index++) {
            let sectionIndex = this.sectionsGroups[index].sections.findIndex((x) => x.name === sectionName);

            if (sectionIndex !== -1) {
                let section = this.sectionsGroups[index].sections[sectionIndex];

                this.sectionsGroups[index].sections.splice(sectionIndex, 1);
                section.resizingStatus = ResizingStatus.Normal;
                this.nonDisplayedSections.push(section);
            }
        }
    }

    /**
     * Affiche la section en plein écran.
     * remarque: L'affichage de la section prend toute la taille de l'écran. Les autres sections sont masquées.
     * @param sectionName : Nom de la section à afficher en plein écran.
     */
    public displaySectionInFullScreen(sectionName: string) {
        this.sectionsGroups.forEach((sectionsGroup, index) => {
            if (sectionsGroup.sections.findIndex((x) => x.name === sectionName) !== -1) {
                sectionsGroup.sections.forEach((section) => {
                    if (section.name === sectionName) {
                        section.resizingStatus = ResizingStatus.Maximized;
                    } else {
                        section.resizingStatus = ResizingStatus.Hidden;
                    }
                });

                sectionsGroup.visible = true;
            } else {
                sectionsGroup.visible = false;
            }
        });
    }

    /**
     * Affiche la section pour qu'elle occupe toute la hauteur de son groupe.
     * remarque: L'affichage de la section prend toute a hauteur de son groupe. Les autres sections du groupe sont masquées.
     * @param sectionName : Nom de la section à afficher.
     */
    public maximizeSectionHeight(sectionName: string) {
        this.sectionsGroups.forEach((sectionsGroup, index) => {
            if (sectionsGroup.sections.findIndex((x) => x.name === sectionName) !== -1) {
                sectionsGroup.sections.forEach((section) => {
                    if (section.name === sectionName) {
                        section.resizingStatus = ResizingStatus.VerticalMaximized;
                    } else {
                        section.resizingStatus = ResizingStatus.Hidden;
                    }
                });
            }
        });
    }

    /**
     * Réinitialise la taille de la section.
     * remarque: La section reprend sa taille normale suite à un affichage en plein écran ou en hauteur maximale. Les sections masquées sont à nouveau affichées.
     * @param sectionName : Nom de la section à réinitialiser.
     */
    public resetSectionSize(sectionName: string) {
        for (let index: number = 0; index < this.sectionsGroups.length; index++) {
            this.sectionsGroups[index].visible = true;
        }

        let sectionGroup = this.getSectionsGroup(sectionName);

        sectionGroup.sections.forEach((section) => {
            section.resizingStatus = ResizingStatus.Normal;
        });
    }

    /**
     * Retournes le groupe auquel appartient une section.
     * @param sectionName : Nom de la section.
     * @returns : Le groupe auquel appartient la section.
     */
    private getSectionsGroup(sectionName: string) {
        for (let index: number = 0; index < this.sectionsGroups.length; index++) {
            let sectionIndex = this.sectionsGroups[index].sections.findIndex((x) => x.name === sectionName);

            if (sectionIndex !== -1) {
                return this.sectionsGroups[index];
            }
        }

        return null;
    }
}
